import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  subscribeToAgentStatus,
  subscribeToAgentResults,
  sendCommandToAgent,
  sendConfigToAgent,
} from '../../../services/agent/agentService';

/**
 * AgentDetailPanel Component
 * Displays detailed information about a selected agent including:
 * - Current status and heartbeat
 * - Command sending interface
 * - Results history
 * - Configuration management
 *
 * @see Section 3 of AGENT_API_CONTRACT.md for message schemas
 */
const AgentDetailPanel = ({ agentId, onClose }) => {
  const [status, setStatus] = useState({
    status: 'unknown',
    timestamp: null,
    features: [],
    lastUpdate: 'Never',
  });
  const [results, setResults] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [activeTab, setActiveTab] = useState('status');
  const [isSending, setIsSending] = useState(false);

  // Command form state
  const [commandForm, setCommandForm] = useState({
    action: '',
    parameters: '{}',
  });

  // Config form state
  const [configForm, setConfigForm] = useState({
    features: '{\n  "sip_bridge": true,\n  "gpio_alert": false\n}',
    settings: '{\n  "poll_interval": 30\n}',
  });

  const resultsEndRef = useRef(null);

  // Subscribe to agent status and results
  useEffect(() => {
    let statusUnsubscribe;
    let resultUnsubscribe;

    const setupSubscriptions = async () => {
      try {
        // Subscribe to agent status messages
        // See Section 3.B of AGENT_API_CONTRACT.md
        statusUnsubscribe = await subscribeToAgentStatus(agentId, statusData => {
          if (statusData.type === 'status') {
            setStatus({
              status: statusData.status,
              timestamp: statusData.timestamp,
              features: statusData.features || [],
              lastUpdate: new Date().toLocaleTimeString(),
            });
            setIsConnected(true);
          }
        });

        // Subscribe to agent result messages
        // See Section 3.D of AGENT_API_CONTRACT.md
        resultUnsubscribe = await subscribeToAgentResults(agentId, resultData => {
          if (resultData.type === 'result') {
            setResults(prev => [resultData, ...prev].slice(0, 50)); // Keep last 50 results

            toast(
              `${resultData.success ? '✅' : '❌'} ${resultData.taskId}: ${resultData.output || 'Task completed'}`,
              { type: resultData.success ? 'success' : 'error' }
            );
          }
        });

        toast.success(`Connected to agent ${agentId}`);
      } catch (error) {
        console.error(`Error subscribing to agent ${agentId}:`, error);
        toast.error(`Failed to connect to agent ${agentId}: ${error.message}`);
        setIsConnected(false);
      }
    };

    setupSubscriptions();

    // Cleanup subscriptions on component unmount
    return () => {
      if (statusUnsubscribe) statusUnsubscribe();
      if (resultUnsubscribe) resultUnsubscribe();
    };
  }, [agentId]);

  // Helper to format timestamp
  const formatTimestamp = timestamp => {
    if (!timestamp) return 'Never';

    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Helper to get status color class
  const getStatusColor = status => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800';
      case 'offline':
        return 'bg-red-100 text-red-800';
      case 'busy':
        return 'bg-yellow-100 text-yellow-800';
      case 'degraded':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Command form handlers
  const handleCommandChange = e => {
    const { name, value } = e.target;
    setCommandForm(prev => ({ ...prev, [name]: value }));
  };

  // Config form handlers
  const handleConfigChange = e => {
    const { name, value } = e.target;
    setConfigForm(prev => ({ ...prev, [name]: value }));
  };

  // Send command to agent
  const handleSendCommand = async () => {
    try {
      setIsSending(true);

      // Validate command action
      if (!commandForm.action.trim()) {
        toast.error('Command action is required');
        return;
      }

      // Parse parameters JSON
      let parameters;
      try {
        parameters = JSON.parse(commandForm.parameters);
      } catch (error) {
        toast.error('Invalid JSON in parameters');
        return;
      }

      // Send command to agent via MQTT
      // See Section 3.A of AGENT_API_CONTRACT.md
      const commandId = await sendCommandToAgent(agentId, commandForm.action, parameters);

      toast.info(`Command sent: ${commandForm.action}`);
      console.log(`Command sent to agent ${agentId}. Command ID: ${commandId}`);
    } catch (error) {
      console.error('Error sending command:', error);
      toast.error(`Failed to send command: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Send config to agent
  const handleSendConfig = async () => {
    try {
      setIsSending(true);

      // Parse features and settings JSON
      let features, settings;
      try {
        features = JSON.parse(configForm.features);
        settings = JSON.parse(configForm.settings);
      } catch (error) {
        toast.error('Invalid JSON in features or settings');
        return;
      }

      // Send config to agent via MQTT
      // See Section 3.C of AGENT_API_CONTRACT.md
      await sendConfigToAgent(agentId, features, settings);

      toast.info('Configuration update sent');
    } catch (error) {
      console.error('Error sending config:', error);
      toast.error(`Failed to send config: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  // Clear results
  const handleClearResults = () => {
    setResults([]);
  };

  // Command templates
  const commandTemplates = [
    {
      name: 'SIP Call',
      action: 'trigger_sip_call',
      parameters: {
        target: '1001',
        alertType: 'emergency',
      },
    },
    {
      name: 'REST API Call',
      action: 'trigger_rest_call',
      parameters: {
        endpoint: '/api/test',
        method: 'GET',
      },
    },
    {
      name: 'Restart Agent',
      action: 'system_restart',
      parameters: {
        delay: 5,
      },
    },
  ];

  // Apply command template
  const applyCommandTemplate = template => {
    setCommandForm({
      action: template.action,
      parameters: JSON.stringify(template.parameters, null, 2),
    });
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gray-50 px-4 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Agent Details: {agentId}</h2>
          <div className="flex items-center mt-1">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}
            >
              {status.status.toUpperCase()}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              Last Updated: {formatTimestamp(status.timestamp)}
            </span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('status')}
            className={`px-4 py-2 text-center text-sm font-medium ${
              activeTab === 'status'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Status
          </button>
          <button
            onClick={() => setActiveTab('command')}
            className={`px-4 py-2 text-center text-sm font-medium ${
              activeTab === 'command'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Send Command
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 text-center text-sm font-medium ${
              activeTab === 'config'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`px-4 py-2 text-center text-sm font-medium ${
              activeTab === 'results'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Results History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {/* Status Tab */}
        {activeTab === 'status' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Agent Info
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span
                      className={`font-medium ${
                        status.status === 'online'
                          ? 'text-green-600'
                          : status.status === 'offline'
                            ? 'text-red-600'
                            : status.status === 'busy'
                              ? 'text-yellow-600'
                              : 'text-gray-600'
                      }`}
                    >
                      {status.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Connection:</span>
                    <span
                      className={`font-medium ${isConnected ? 'text-green-600' : 'text-red-600'}`}
                    >
                      {isConnected ? 'Active' : 'Disconnected'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Heartbeat:</span>
                    <span className="font-medium">{formatTimestamp(status.timestamp)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">UI Updated:</span>
                    <span className="font-medium">{status.lastUpdate}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                  Features
                </h3>
                {status.features && status.features.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {status.features.map((feature, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No features reported</p>
                )}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                Recent Activity
              </h3>
              {results.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {results.slice(0, 5).map((result, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded-md ${result.success ? 'bg-green-50' : 'bg-red-50'}`}
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{result.taskId}</span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(result.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{result.output}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No recent activity</p>
              )}
            </div>
          </div>
        )}

        {/* Command Tab */}
        {activeTab === 'command' && (
          <div>
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Command Templates</h3>
              <div className="flex flex-wrap gap-2">
                {commandTemplates.map((template, index) => (
                  <button
                    key={index}
                    onClick={() => applyCommandTemplate(template)}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700"
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="action" className="block text-sm font-medium text-gray-700 mb-1">
                  Command Action
                </label>
                <input
                  type="text"
                  id="action"
                  name="action"
                  value={commandForm.action}
                  onChange={handleCommandChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., trigger_sip_call"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Action name defined in the agent API contract (e.g., trigger_sip_call,
                  system_restart)
                </p>
              </div>

              <div>
                <label
                  htmlFor="parameters"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Parameters (JSON)
                </label>
                <textarea
                  id="parameters"
                  name="parameters"
                  rows="8"
                  value={commandForm.parameters}
                  onChange={handleCommandChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
                  placeholder="{}"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Command parameters in JSON format, according to the agent API contract
                </p>
              </div>

              <button
                type="button"
                onClick={handleSendCommand}
                disabled={isSending || !isConnected}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
              >
                {isSending ? 'Sending...' : 'Send Command'}
              </button>
            </div>
          </div>
        )}

        {/* Config Tab */}
        {activeTab === 'config' && (
          <div className="space-y-4">
            <div>
              <label htmlFor="features" className="block text-sm font-medium text-gray-700 mb-1">
                Features (JSON)
              </label>
              <textarea
                id="features"
                name="features"
                rows="6"
                value={configForm.features}
                onChange={handleConfigChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                Feature toggles in JSON format (e.g.,{' '}
                {`{ "sip_bridge": true, "gpio_alert": false }`})
              </p>
            </div>

            <div>
              <label htmlFor="settings" className="block text-sm font-medium text-gray-700 mb-1">
                Settings (JSON)
              </label>
              <textarea
                id="settings"
                name="settings"
                rows="6"
                value={configForm.settings}
                onChange={handleConfigChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                Agent settings in JSON format (e.g.,{' '}
                {`{ "poll_interval": 30, "sip_user": "agent001" }`})
              </p>
            </div>

            <button
              type="button"
              onClick={handleSendConfig}
              disabled={isSending || !isConnected}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {isSending ? 'Sending...' : 'Send Configuration Update'}
            </button>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Results History</h3>
              <button
                onClick={handleClearResults}
                className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded text-gray-700"
              >
                Clear Results
              </button>
            </div>

            <div className="bg-gray-50 rounded-md border border-gray-200 p-2 h-96 overflow-y-auto">
              {results.length === 0 ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>No results received yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {results.map((result, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-md text-sm ${
                        result.success
                          ? 'bg-green-50 border border-green-100'
                          : 'bg-red-50 border border-red-100'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-medium">Task: </span>
                          <span className="font-mono">{result.taskId}</span>
                        </div>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded-full ${
                            result.success
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {result.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                      <div className="mt-1">
                        <span className="font-medium">Output: </span>
                        <span>{result.output}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {formatTimestamp(result.timestamp)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div ref={resultsEndRef} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

AgentDetailPanel.propTypes = {
  agentId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AgentDetailPanel;
